import { useCallback, useMemo } from 'react';
import { isEmpty } from 'lodash';
import { Rows } from 'common/types/scalarSpreadsheet';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import {
  buildDailyNavColumnWidths,
  DAILY_NAV_SPREADSHEET_NAME,
  dailyNavCompanyRowAlias,
  dailyNavInvestmentRowAlias,
} from 'pages/Intelligence/DailyNav/daily-nav.constants';
import { getArrayValue } from 'utilities';
import { createColumns, createParser, createRows } from './config';
// Not re-exported from './config' (its barrel is out of scope for this change) — the
// column-type order is derived from the rows, since Secondary Data's per-provider price
// columns are discovered per response rather than statically configured.
import { buildDailyNavColumnTypeOrder } from './config/createColumns/createColumns';
import {
  DailyNavColumn,
  DailyNavSpreadsheet,
  DailyNavTableRow,
  UseDailyNavSheetParams,
  UseDailyNavSheetResult,
} from './useDailyNavSheet.types';

const useDailyNavSheet = (params: UseDailyNavSheetParams): UseDailyNavSheetResult => {
  const {
    companies,
    firmSlug,
    records,
    onReviewClick,
    canReview,
    onOverrideClick,
    onBreakdownClick,
    onSecondaryBreakdownClick,
    onClosedTransactionsClick,
    onNewsClick,
  } = params;

  const dailyNavRows = useMemo<DailyNavTableRow[]>(
    () =>
      getArrayValue(records).reduce<DailyNavTableRow[]>((accumulator, record) => {
        const company = getArrayValue(companies).find(item => item.company_id === record.company);
        if (!company) {
          return accumulator;
        }

        // Merged server-side from the opening and today's valuations; the Excel
        // export lists the same rows. See daily_nav services/marks/holdings.py.
        const holdings = record.holdings ?? [];
        accumulator.push({
          alias: dailyNavCompanyRowAlias(record.company),
          kind: 'company',
          companyId: record.company,
          companyName: company.company_name,
          companySlug: company.company_slug,
          record,
          // Rides on the record itself now, keyed by company server-side, so the
          // sheet no longer joins a second response by company_id.
          previousValuation: record.previous_valuation,
          holdings,
        });
        holdings.forEach(holding => {
          accumulator.push({
            alias: dailyNavInvestmentRowAlias(record.company, holding.fund_id, holding.security_id),
            kind: 'fund_security',
            companyId: record.company,
            companyName: company.company_name,
            companySlug: company.company_slug,
            record,
            // Date still comes from the parent band.
            previousValuation: record.previous_valuation,
            fundId: holding.fund_id,
            fundName: holding.fund_name,
            securityId: holding.security_id,
            securityName: holding.security_name,
            weightedShareValue: holding.previous_share_value,
            todaysShareValue: holding.todays_share_value,
            holdingChange: holding.holding_change ?? undefined,
          });
        });
        return accumulator;
      }, []),
    [companies, records]
  );

  const rows = useMemo<Rows>(() => createRows({ firmSlug, rows: dailyNavRows }), [dailyNavRows, firmSlug]);

  const columns = useMemo<DailyNavColumn[]>(() => {
    if (isEmpty(dailyNavRows)) {
      return [];
    }
    return createColumns(dailyNavRows);
  }, [dailyNavRows]);

  // Recomputed from the same rows createColumns used, so the two always agree on which
  // per-provider Secondary Data price columns exist and in what order.
  const columnTypeOrder = useMemo<string[]>(() => buildDailyNavColumnTypeOrder(dailyNavRows), [dailyNavRows]);

  const columnWidths = useMemo(() => buildDailyNavColumnWidths(columnTypeOrder), [columnTypeOrder]);

  const parser = useCallback(
    () =>
      createParser({
        columns,
        rowConfig: rows,
        dailyNavRows,
        onReviewClick,
        columnTypeOrder,
        canReview,
        onOverrideClick,
        onBreakdownClick,
        onSecondaryBreakdownClick,
        onClosedTransactionsClick,
        onNewsClick,
      }),
    [
      columns,
      rows,
      dailyNavRows,
      onReviewClick,
      columnTypeOrder,
      canReview,
      onOverrideClick,
      onBreakdownClick,
      onSecondaryBreakdownClick,
      onClosedTransactionsClick,
      onNewsClick,
    ]
  );

  const spreadsheet = useMemo<DailyNavSpreadsheet | null>(() => {
    if (isEmpty(columns)) {
      return null;
    }

    // Constructed from a plain config object rather than satisfying SpreadsheetConfigConstructorParams' full
    // generic signature exactly, matching the same pattern used by useFirmSummarySheet.
    const spreadsheetConfig = new SpreadsheetConfig({
      allowConfirmAndDeleteColumn: false,
      allowDeleteRow: false,
      // Row 0 is the section band (Previous Valuation / Today / …); row 1 holds the column labels.
      colTitleRow: 1,
      columns,
      hasColTitle: true,
      name: DAILY_NAV_SPREADSHEET_NAME,
      page: DAILY_NAV_SPREADSHEET_NAME,
      parser,
      reverseParser: undefined,
      rowConfig: rows,
      showPreviousColsDivider: false,
      showTitlesColumn: true,
      showToolbar: true,
      showTotalColumn: false,
      tableData: { columns },
      totalParser: undefined,
    } as unknown as ConstructorParameters<typeof SpreadsheetConfig>[0]) as DailyNavSpreadsheet;

    return spreadsheetConfig;
  }, [columns, parser, rows]);

  return {
    spreadsheet,
    columnTypes: columnTypeOrder,
    columnWidths,
  };
};

export default useDailyNavSheet;
